import { approvalSequence, columnMap, PermissionType, requiredCellChecks } from 'src/app/model';

function hasCellValue(value: unknown): boolean {
  return value !== null && value !== undefined && value.toString().trim() !== '';
}

export async function checkApprovalSequence(
  permission: PermissionType,
  selectedRows: any[]
): Promise<boolean> {
  const currentIndex = approvalSequence.indexOf(permission);
  if (currentIndex <= 0) return true;

  const priorPermission = approvalSequence[currentIndex - 1];
  const priorCols = columnMap[priorPermission];

  const isRowValid = (row: any): boolean => {
    const priorUserValue = row[priorCols.userColName];
    const priorDateValue = row[priorCols.dateColName];
    return hasCellValue(priorUserValue) && hasCellValue(priorDateValue);
  };

  return selectedRows.every(isRowValid);
}

export async function checkRequiredCell(
  permission: PermissionType,
  selectedRows: any[]
): Promise<{ isValid: boolean; errorMessage?: string }> {
  const requiredChecks = requiredCellChecks[permission];
  
  if (!requiredChecks || requiredChecks.length === 0) {
    return { isValid: true }; // No additional check required for this permission
  }

  // Check each required field
  for (const requiredCheck of requiredChecks) {
    const isRowValid = (row: any): boolean => {
      const cellValue = row[requiredCheck.colName];
      return hasCellValue(cellValue);
    };

    const isValid = selectedRows.every(isRowValid);
    
    if (!isValid) {
      return {
        isValid: false,
        errorMessage: requiredCheck.errorMessage
      };
    }
  }
  
  return { isValid: true };
}

export function checkRowsHaveValue(
  selectedRows: any[],
  columnName: string
): boolean {
  return selectedRows.every((row) => hasCellValue(row[columnName]));
}

export function getLatestSignOffPermission(row: any): PermissionType | null {
  for (let index = approvalSequence.length - 1; index >= 0; index--) {
    const permission = approvalSequence[index];
    const columns = columnMap[permission];

    if (hasCellValue(row[columns.userColName]) && hasCellValue(row[columns.dateColName])) {
      return permission;
    }
  }

  return null;
}

export async function checkUndoHasCurrentSignOff(
  permission: PermissionType,
  selectedRows: any[]
): Promise<boolean> {
  const targetCols = columnMap[permission];

  return selectedRows.every((row) =>
    hasCellValue(row[targetCols.userColName]) &&
    hasCellValue(row[targetCols.dateColName])
  );
}

export async function checkUndoSequence(
  permission: PermissionType,
  selectedRows: any[]
): Promise<boolean> {
  return selectedRows.every((row) => getLatestSignOffPermission(row) === permission);
}

export function getCommonLatestSignOffPermission(
  selectedRows: any[]
): PermissionType | null {
  if (!selectedRows.length) {
    return null;
  }

  const firstLatestPermission = getLatestSignOffPermission(selectedRows[0]);
  if (!firstLatestPermission) {
    return null;
  }

  const hasCommonLatestPermission = selectedRows.every(
    (row) => getLatestSignOffPermission(row) === firstLatestPermission
  );

  return hasCommonLatestPermission ? firstLatestPermission : null;
}